import React, { useState, useEffect, useRef } from 'react';
import { Image as ImageIcon, Loader2, AlertCircle, Download, Instagram, X, Plus, Minus, ChevronLeft, ChevronRight, Trash2, Filter, Check, Copy, Share2, Lock, Heart, Pencil, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { supabase } from '../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../config';
import SubscriptionLimitModal from '../components/SubscriptionLimitModal';
import { trackInteraction } from '../utils/trackInteraction';

interface GeneratedImage {
  id: number;
  file_name: string;
  created_at: string;
  variante_id: number;
  variante_nombre: string;
  formato?: string; // Format of the image (e.g., 'square', 'portrait', 'landscape')
  tipo?: string; // Type of content (e.g., 'video', 'video_pro' for videos)
  favorito?: boolean;
  signedUrl?: string;
  thumbnailUrl?: string;
  filePath?: string; // Add filePath to store the direct path for the image
}

// Helper to detect if an item is a video based on tipo column
const isVideoItem = (item?: GeneratedImage | null): boolean => {
  if (!item?.tipo) return false;
  return item.tipo === 'video' || item.tipo === 'video_pro';
};

// Helper to format date in a smart way
const formatSmartDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  
  // Reset time to start of day for comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const dayBeforeYesterday = new Date(today);
  dayBeforeYesterday.setDate(dayBeforeYesterday.getDate() - 2);
  const oneWeekAgo = new Date(today);
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const timeStr = `${hours}:${minutes} hs`;
  
  // Today
  if (dateOnly.getTime() === today.getTime()) {
    return `Hoy ${timeStr}`;
  }
  
  // Yesterday
  if (dateOnly.getTime() === yesterday.getTime()) {
    return `Ayer ${timeStr}`;
  }
  
  // Day before yesterday
  if (dateOnly.getTime() === dayBeforeYesterday.getTime()) {
    return `Anteayer ${timeStr}`;
  }
  
  // Within the last week - show day name
  if (dateOnly >= oneWeekAgo) {
    const dayNames = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    return `${dayNames[date.getDay()]} ${timeStr}`;
  }
  
  // Older - show day and month
  const monthNames = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  return `${date.getDate()} de ${monthNames[date.getMonth()]}`;
};

export const GeneratedImagesHistory = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false); // New state for loading more images
  const [error, setError] = useState<string | null>(null);
  const [allImages, setAllImages] = useState<GeneratedImage[]>([]);
  const [filteredImages, setFilteredImages] = useState<GeneratedImage[]>([]);
  const [variants, setVariants] = useState<{ id: number; nombre: string }[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<number | null>(null);
  const [hasMoreImages, setHasMoreImages] = useState(true); // Track if more images are available
  const [currentPage, setCurrentPage] = useState(0); // Track current page for pagination
  const PAGE_SIZE = 32; // Number of images to fetch per page
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyUuid, setCompanyUuid] = useState<string | null>(null);
  // States for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<GeneratedImage | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Mobile filter states
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  // Timeframe options for the filter
  type TimeframeOption = 'today' | 'last7days' | 'last30days' | 'last90days';

  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeOption>('last30days');
  const [onlyFavorites, setOnlyFavorites] = useState<boolean>(false);
  // Mobile drafts for filters (apply only on button press)
  const [mobileSelectedTimeframe, setMobileSelectedTimeframe] = useState<TimeframeOption>(selectedTimeframe);
  const [mobileSelectedVariant, setMobileSelectedVariant] = useState<number | null>(selectedVariant);
  const [mobileOnlyFavorites, setMobileOnlyFavorites] = useState<boolean>(onlyFavorites);

  // Calculate date range based on selected timeframe
  const getDateRangeFromTimeframe = (timeframe: TimeframeOption) => {
    const now = new Date();
    const end = new Date(now);
    end.setHours(23, 59, 59, 999); // End of current day
    
    let start = new Date(now);
    
    switch (timeframe) {
      case 'today':
        start.setHours(0, 0, 0, 0); // Start of current day
        break;
      case 'last7days':
        start.setDate(start.getDate() - 6); // Last 7 days (today + 6 previous days)
        start.setHours(0, 0, 0, 0);
        break;
      case 'last30days':
        start.setDate(start.getDate() - 29); // Last 30 days (today + 29 previous days)
        start.setHours(0, 0, 0, 0);
        break;
      case 'last90days':
      default:
        start.setDate(start.getDate() - 89); // Last 90 days (today + 89 previous days)
        start.setHours(0, 0, 0, 0);
        break;
    }
  
    return {
      start: start.toISOString(),
      end: end.toISOString()
    };
  };

  // Toggle favorite status for a given image
  const handleToggleFavorite = async (image: GeneratedImage, e?: React.MouseEvent) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!image) return;
    if (!companyId) {
      console.error('No companyId available to update favorito');
      return;
    }
    try {
      setIsFavoriting(true);
      const nextValue = !(image.favorito === true);
      const { error } = await supabase
        .from('imagenes_generadas')
        .update({ favorito: nextValue })
        .eq('id', image.id)
        .eq('id_company', companyId);
      if (error) {
        console.error('Error actualizando favorito:', error);
        alert('No se pudo actualizar favorito');
        return;
      }

      // Update local collections
      const currentFiltered = filteredImages;
      const currentAll = allImages;
      const newAll = currentAll.map((img) => (img.id === image.id ? { ...img, favorito: nextValue } : img));
      let newFiltered: GeneratedImage[] = [];
      if (onlyFavorites && !nextValue) {
        // Remove from filtered if we are showing only favorites and it was unfavorited
        newFiltered = currentFiltered.filter((img) => img.id !== image.id);
      } else {
        newFiltered = currentFiltered.map((img) => (img.id === image.id ? { ...img, favorito: nextValue } : img));
      }
      setAllImages(newAll);
      setFilteredImages(newFiltered);

      // Update selected image if open
      if (selectedImage && selectedImage.id === image.id) {
        if (onlyFavorites && !nextValue) {
          if (newFiltered.length > 0) {
            const currentIndex = currentFiltered.findIndex((img) => img.id === image.id);
            const nextIndex = currentIndex >= newFiltered.length ? 0 : currentIndex;
            const nextImg = newFiltered[nextIndex];
            setSelectedImage(nextImg);
          } else {
            setSelectedImage(null);
          }
          setZoomLevel(1);
          setDragOffset({ x: 0, y: 0 });
        } else {
          setSelectedImage({ ...selectedImage, favorito: nextValue });
        }
      }
    } catch (e) {
      console.error('Error marcando favorito:', e);
      alert('No se pudo actualizar favorito');
    } finally {
      setIsFavoriting(false);
    }
  }

  // Determine if Instagram is connected using company schema instagram_data
  const checkInstagramConnection = async () => {
    try {
      const { data: auth } = await supabase.auth.getUser();
      const user = auth?.user;
      if (!user) {
        setIsInstagramConnected(false);
        return;
      }

      // Check company schema instagram_data (single source of truth)
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (profile?.id_company) {
        const { data: instagramData } = await supabase
          .from('instagram_data')
          .select('instagram_username')
          .eq('id_company', profile.id_company)
          .maybeSingle();

        if (instagramData?.instagram_username) {
          setIsInstagramConnected(true);
          return;
        }
      }

      setIsInstagramConnected(false);
    } catch (e) {
      console.error('Error checking Instagram connection:', e);
      setIsInstagramConnected(false);
    }
  };
  
  // Initialize date range from selected timeframe
  const [dateRange, setDateRange] = useState(getDateRangeFromTimeframe('last30days'));
  const [selectedImage, setSelectedImage] = useState<GeneratedImage | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [loadingThumbnails, setLoadingThumbnails] = useState<Record<number, boolean>>({});
  
  // Instagram posting states
  const [showInstagramPreview, setShowInstagramPreview] = useState<boolean>(false);

  // Debug log: Compare variant ids and image variante_ids
  useEffect(() => {
    if (allImages.length > 0 && variants.length > 0) {
      console.log('DEBUG - Variants in dropdown:', variants.map(v => v.id));
      console.log('DEBUG - Images variante_id:', allImages.map(img => img.variante_id));
    }
  }, [allImages, variants]);
  const [instagramUsername, setInstagramUsername] = useState('instagram_username');
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [isPostingToInstagram, setIsPostingToInstagram] = useState(false);
  const [instagramPostSuccess, setInstagramPostSuccess] = useState(false);
  const [postDescription, setPostDescription] = useState('Moda fashion . veni a visitarnos');
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(postDescription);
  const imageContainerRef = useRef<HTMLImageElement>(null);
  const [isInstagramConnected, setIsInstagramConnected] = useState<boolean>(false);
  const [showConnectPrompt, setShowConnectPrompt] = useState<boolean>(false);
  const [isFavoriting, setIsFavoriting] = useState<boolean>(false);
  const [showSubscriptionLimitModal, setShowSubscriptionLimitModal] = useState<boolean>(false);
  const [subscriptionLimitType, setSubscriptionLimitType] = useState<'free-trial-ended' | 'plan-limit-reached'>('free-trial-ended');
  const [showEditPlanRestriction, setShowEditPlanRestriction] = useState<boolean>(false);
  const [showDescriptionPanel, setShowDescriptionPanel] = useState<boolean>(false);
  const [copiedPanel, setCopiedPanel] = useState<boolean>(false);
  const [copiedPreview, setCopiedPreview] = useState<boolean>(false);
  const [progressActive, setProgressActive] = useState<boolean>(false);

  // Fetch Instagram username from instagram_data table
  const fetchInstagramData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();
      
      if (!profile?.id_company) return;
      
      const { data: instagramData, error: instagramError } = await supabase
        .from('instagram_data')
        .select('instagram_username')
        .eq('id_company', profile.id_company)
        .maybeSingle();
      
      if (instagramData?.instagram_username) {
        setInstagramUsername(instagramData.instagram_username);
      } else {
        console.error('Error fetching Instagram data:', instagramError);
        setInstagramUsername('usuario_instagram');
      }
    } catch (err) {
      console.error('Error fetching Instagram data:', err);
      setInstagramUsername('usuario_instagram');
    }
  };
  
  // Fetch company logo
  const fetchCompanyLogo = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();
      
      if (profile?.id_company) {
        const { data: companyData } = await supabase
          .from('company_profiles')
          .select('logo_url')
          .eq('id_company', profile.id_company)
          .single();
          
        if (companyData?.logo_url) {
          setCompanyLogo(companyData.logo_url);
        }
      }
    } catch (err) {
      console.error('Error fetching company logo:', err);
    }
  };

  // Function to generate a description for the Instagram post
  const generateDescription = async () => {
    if (!selectedImage) return;
    
    setIsGeneratingDescription(true);
    setPostDescription("Generando descripción...");
    
    try {
      // Get the user from Supabase auth
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        alert('No se pudo identificar al usuario');
        setPostDescription("Moda fashion . veni a visitarnos");
        setIsGeneratingDescription(false);
        return;
      }
      
      const response = await fetch("https://ai-agency-dt7d.onrender.com/webhook/0d2c7586-b0b6-45c8-94e3-ab1ff6968e7c", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          imagen_id: selectedImage.id,
          mode: "generate_description"
        })
      });
      
      const data = await response.json();
      
      if (data.response === "success") {
        // Find company ID for the schema
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
          
        if (!profile?.id_company) {
          alert('No se pudo identificar la empresa');
          setPostDescription("Moda fashion . veni a visitarnos");
          setIsGeneratingDescription(false);
          return;
        }

        // Fetch the generated description from the database
        const { data: imageData, error } = await supabase
          .from('imagenes_generadas')
          .select('post_description')
          .eq('id', selectedImage.id)
          .eq('id_company', profile.id_company)
          .single();
          
        if (error) {
          console.error("Error fetching the description:", error);
          alert("Error al obtener la descripción generada");
          setPostDescription("Moda fashion . veni a visitarnos");
        } else if (imageData?.post_description) {
          setPostDescription(imageData.post_description);
        } else {
          setPostDescription("Moda fashion . veni a visitarnos");
        }
      } else {
        alert("Error al generar la descripción");
        setPostDescription("Moda fashion . veni a visitarnos");
      }
    } catch (error) {
      console.error("Error generating description:", error);
      alert("Error al generar la descripción");
      setPostDescription("Moda fashion . veni a visitarnos");
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Reset description panel when navigating to another image
  useEffect(() => {
    setShowDescriptionPanel(false);
    setIsGeneratingDescription(false);
    setPostDescription('');
  }, [selectedImage?.id]);

  // Trigger non-linear bottom progress bar when generating description
  useEffect(() => {
    if (showDescriptionPanel && isGeneratingDescription) {
      setProgressActive(false);
      const raf = requestAnimationFrame(() => setProgressActive(true));
      return () => cancelAnimationFrame(raf);
    } else {
      setProgressActive(false);
    }
  }, [showDescriptionPanel, isGeneratingDescription]);

  // Function to post to Instagram
  const handlePostToInstagram = async () => {
    if (!selectedImage) return;
    
    setIsPostingToInstagram(true);
    setInstagramPostSuccess(false);
    
    try {
      // Get the user from Supabase auth
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        alert('No se pudo identificar al usuario');
        setIsPostingToInstagram(false);
        return;
      }
      
      const response = await fetch("https://ai-agency-dt7d.onrender.com/webhook/0d2c7586-b0b6-45c8-94e3-ab1ff6968e7c", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.id,
          imagen_id: selectedImage.id,
          mode: "post"
        })
      });
      
      const data = await response.json();
      
      if (data.response === "success") {
        setInstagramPostSuccess(true);
        // No need for alert as we'll show the visual success message
      } else {
        alert("Error al publicar en Instagram");
      }
    } catch (error) {
      console.error("Error posting to Instagram:", error);
      alert("Error al publicar en Instagram");
    } finally {
      setIsPostingToInstagram(false);
    }
  };

  // Copy Instagram post description to clipboard
  const copyDescription = async (scope: 'panel' | 'preview' = 'panel') => {
    try {
      await navigator.clipboard.writeText(postDescription);
      if (scope === 'panel') {
        setCopiedPanel(true);
        setTimeout(() => setCopiedPanel(false), 2000);
      } else {
        setCopiedPreview(true);
        setTimeout(() => setCopiedPreview(false), 2000);
      }
    } catch (error) {
      console.error("Error al copiar la descripción:", error);
      alert("No se pudo copiar la descripción");
    }
  };

  // Poll DB for post_description after webhook returns success (handles async update)
  const waitForPostDescription = async (
    idCompany: string,
    imageId: number,
    maxWaitMs = 15000,
    intervalMs = 800
  ): Promise<string | null> => {
    const deadline = Date.now() + maxWaitMs;
    while (Date.now() < deadline) {
      try {
        // Abort if user navigated to another image
        if (!selectedImage || selectedImage.id !== imageId) return null;
        const { data: imageData } = await supabase
          .from('imagenes_generadas')
          .select('post_description')
          .eq('id', imageId)
          .eq('id_company', idCompany)
          .single();
        const desc = (imageData as any)?.post_description as string | undefined;
        if (desc && desc.trim().length > 0) {
          return desc;
        }
      } catch {}
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    return null;
  };

  const handleShowDescription = async (e?: React.MouseEvent) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!selectedImage) return;
    setShowDescriptionPanel(true);
    setCopiedPanel(false);
    setIsGeneratingDescription(true);
    setPostDescription('Generando descripción para post de Instagram');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setPostDescription('No se pudo generar la descripción. Intentá nuevamente.');
        setIsGeneratingDescription(false);
        return;
      }
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();
      let idCompany = (profile as any)?.id_company ?? null;
      if (!idCompany && companyId) {
        idCompany = companyId;
      }
      if (!idCompany) {
        setPostDescription('No se pudo generar la descripción. Intentá nuevamente.');
        setIsGeneratingDescription(false);
        return;
      }
      const { data: existing } = await supabase
        .from('imagenes_generadas')
        .select('post_description')
        .eq('id', selectedImage.id)
        .eq('id_company', idCompany)
        .single();
      if ((existing as any)?.post_description) {
        setPostDescription((existing as any).post_description as string);
        setIsGeneratingDescription(false);
        return;
      }
      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/descripcion_instagram', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
        body: JSON.stringify({ user_id: user.id, image_id: selectedImage.id }),
      });
      let ok = false;
      try {
        const data = await response.json();
        ok = data?.response === 'success';
      } catch {
        ok = false;
      }
      if (ok) {
        const finalText = await waitForPostDescription(idCompany, selectedImage.id);
        if (finalText) {
          setPostDescription(finalText);
        } else {
          setPostDescription('No se pudo obtener la descripción todavía. Probá nuevamente en unos segundos.');
        }
      } else {
        setPostDescription('No se pudo generar la descripción. Intentá nuevamente.');
      }
    } catch (_e) {
      setPostDescription('No se pudo generar la descripción. Intentá nuevamente.');
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  // Handle clicking the Publicar button to generate description and show Instagram preview
  const handlePostButton = (image: GeneratedImage | null) => {
    if (!image) return;
    
    console.log('Post selected to Instagram:', image.id);

    // Set this image as the current selected image
    setSelectedImage(image);
    
    // Show Instagram preview
    setShowInstagramPreview(true);
    
    // Generate description for this image
    if (!instagramPostSuccess) {
      generateDescription();
    }
  };

  const getCurrentMonthRange = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const getBillingCycleRange = (paidPlanStartDate: string) => {
    const now = new Date();
    const subscriptionDate = new Date(paidPlanStartDate);
    const anchorDay = subscriptionDate.getDate();
    const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const anchorFor = (year: number, month: number) => {
      const dim = daysInMonth(year, month);
      const day = Math.min(anchorDay, dim);
      return new Date(year, month, day, 0, 0, 0, 0);
    };
    let start = anchorFor(now.getFullYear(), now.getMonth());
    if (start.getTime() > now.getTime()) {
      const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      start = anchorFor(prevMonth.getFullYear(), prevMonth.getMonth());
    }
    if (start.getTime() < subscriptionDate.getTime()) {
      start = new Date(subscriptionDate.getFullYear(), subscriptionDate.getMonth(), subscriptionDate.getDate(), 0, 0, 0, 0);
    }
    const nextMonth = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    const end = anchorFor(nextMonth.getFullYear(), nextMonth.getMonth());
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const checkSubscriptionLimitsForEdit = async (): Promise<boolean> => {
    try {
      if (!companyId) return true;
      const { data: companyProfile } = await supabase
        .from('company_profiles')
        .select('plan, imagenes_restantes, paid_plan_start_date')
        .eq('id_company', companyId)
        .single();
      if (!companyProfile) return true;
      if ((companyProfile as any).plan === 'emprendimiento') {
        setShowEditPlanRestriction(true);
        return false;
      }
      const remaining = (companyProfile as any).imagenes_restantes as number | null | undefined;
      if (typeof remaining === 'number' && remaining <= 0) {
        const isFree = (companyProfile as any).plan === 'gratis';
        setSubscriptionLimitType(isFree ? 'free-trial-ended' : 'plan-limit-reached');
        setShowSubscriptionLimitModal(true);
        return false;
      }
      return true;
    } catch {
      return true;
    }
  };

  const handleEditImage = async (image: GeneratedImage | null, e?: React.MouseEvent) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!image || !companyId) return;
    const canEdit = await checkSubscriptionLimitsForEdit();
    if (!canEdit) return;
    try { trackInteraction('imagenes_editadas'); } catch {}
    // Navigate to video editor if it's a video, otherwise image editor
    if (isVideoItem(image)) {
      navigate(`/redes-sociales/editar-video?id=${image.id}&companyId=${companyId}`);
    } else {
      navigate(`/redes-sociales/editar-imagen?id=${image.id}&companyId=${companyId}`);
    }
  };

  // Use a ref to track initialization
  const initialLoadRef = useRef(false);
  const filterChangeRef = useRef({
    variant: selectedVariant,
    onlyFavorites,
    dateStart: dateRange?.start,
    dateEnd: dateRange?.end
  });
  
  // Create ref for infinite scroll observation
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Fetch images and Instagram data on component mount
  useEffect(() => {
    if (!initialLoadRef.current) {
      initialLoadRef.current = true;
      fetchImages();
      fetchInstagramData();
      fetchCompanyLogo();
      checkInstagramConnection();
      try { trackInteraction('historial'); } catch {}
    }
  }, []);
  // Sync mobile drafts when opening the mobile filters panel
  useEffect(() => {
    if (showMobileFilters) {
      setMobileSelectedTimeframe(selectedTimeframe);
      setMobileSelectedVariant(selectedVariant);
      setMobileOnlyFavorites(onlyFavorites);
    }
  }, [showMobileFilters, selectedTimeframe, selectedVariant, onlyFavorites]);
  
  // Handle filter changes separately to avoid loops
  useEffect(() => {
    // Skip the first render
    if (initialLoadRef.current) {
      // Check if filters actually changed
      const prevFilters = filterChangeRef.current;
      if (prevFilters.variant !== selectedVariant ||
          prevFilters.onlyFavorites !== onlyFavorites ||
          prevFilters.dateStart !== dateRange?.start ||
          prevFilters.dateEnd !== dateRange?.end) {
        
        // Update the ref with new values
        filterChangeRef.current = {
          variant: selectedVariant,
          onlyFavorites,
          dateStart: dateRange?.start,
          dateEnd: dateRange?.end
        };
        
        // Reset pagination when filters change
        setCurrentPage(0);
        setAllImages([]);
        setFilteredImages([]);
        setHasMoreImages(true); // Assume there are more images until we check
        fetchImages();
      }
    }
  }, [selectedVariant, onlyFavorites, dateRange]);

  // (No longer need client-side applyFilters useEffect, as filtering is now server-side)

  
  // Update dateRange when timeframe selection changes
  useEffect(() => {
    setDateRange(getDateRangeFromTimeframe(selectedTimeframe));
    
    // When timeframe changes, we might need to reset hasMoreImages
    // If we know we've previously loaded images that didn't fill all pages,
    // there might be more to load with the new filter
    if (allImages.length > 0 && allImages.length % PAGE_SIZE === 0) {
      setHasMoreImages(true);
    }
  }, [selectedTimeframe, allImages.length, PAGE_SIZE]);

  const fetchImages = async (isLoadingMore = false) => {
    try {
      if (isLoadingMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      const idCompany = (profile as any)?.id_company ?? null;
      if (!idCompany) throw new Error('No company associated with user');

      setCompanyId(idCompany);
      setCompanyUuid(idCompany);

      // Fetch variants first if not already loaded
      if (variants.length === 0) {
        const { data: variantsData } = await supabase
          .from('variantes')
          .select('id, nombre')
          .eq('id_company', idCompany)
          .order('nombre');
        setVariants(variantsData || []);
      }

      // Build the query for images with pagination and filters
      let query = supabase
        .from('imagenes_generadas')
        .select(`
          id,
          file_name,
          created_at,
          variante_id,
          formato,
          tipo,
          favorito,
          active,
          variantes:variantes(nombre)
        `)
        .eq('id_company', idCompany)
        .eq('active', true)
        .order('created_at', { ascending: false });

      // Server-side filters
      if (selectedVariant !== null) {
        query = query.eq('variante_id', selectedVariant);
      }
      if (onlyFavorites) {
        query = query.eq('favorito', true);
      }
      if (dateRange && dateRange.start && dateRange.end) {
        query = query.gte('created_at', dateRange.start).lte('created_at', dateRange.end);
      }

      if (isLoadingMore) {
        // If loading more, use the current page to calculate the range
        const startIndex = currentPage * PAGE_SIZE;
        const endIndex = startIndex + PAGE_SIZE - 1;
        query = query.range(startIndex, endIndex);
      } else {
        // Initial load - get first page
        query = query.range(0, PAGE_SIZE - 1);
      }

      const { data: imagesData, error: imagesError } = await query;

      if (imagesError) throw imagesError;

      // Create loading state for thumbnails
      const loadingThumbnailsMap: Record<number, boolean> = {};
      
      // Process images and get both full-size and thumbnail URLs
      const processedImages = await Promise.all((imagesData || []).map(async (img: any) => {
        // Set loading state for this image
        loadingThumbnailsMap[img.id] = true;
        
        // Check if this is a video file
        const isVideo = img.tipo === 'video' || img.tipo === 'video_pro' || 
          (img.file_name && img.file_name.toLowerCase().endsWith('.mp4'));
        
        // Sequential path checking to reduce API calls
        // Define possible file paths - we'll check them in order
        const basePrefix = `${idCompany}`;
        
        // For videos, the thumbnail has the same name but with .png extension
        const fileName = img.file_name ?? '';
        const thumbnailFileName = isVideo ? fileName.replace(/\.mp4$/i, '.png') : fileName;
        
        const optFilePath = `${basePrefix}/opt/${thumbnailFileName}`.replace(/\/+/, '/').replace(/\/+/g, '/');
        const fullFilePath = `${basePrefix}/full/${fileName}`.replace(/\/+/, '/').replace(/\/+/g, '/');
        
        let signedUrl = '';
        let thumbnailUrl = '';
        
        try {
          // STEP 1: First try to get the optimized version for thumbnails (for videos, this is the .png thumbnail)
          console.log('Trying optimized version first:', optFilePath);
          const { data: optData, error: optError } = await supabase
            .storage
            .from('generadas')
            .createSignedUrl(optFilePath, 3600);
          
          // If optimized version found, use it for thumbnail
          if (!optError && optData?.signedUrl) {
            thumbnailUrl = optData.signedUrl;
            console.log('Found optimized image for thumbnail');
            
            // STEP 2: Now get the full-size image (only if we found thumbnail first)
            const { data: fullData, error: fullError } = await supabase
              .storage
              .from('generadas')
              .createSignedUrl(fullFilePath, 3600);
            
            if (!fullError && fullData?.signedUrl) {
              signedUrl = fullData.signedUrl;
              console.log('Found full-size image');
            } else {
              // If full size not found:
              // - For videos: DON'T use thumbnail as signedUrl (it's a .png, not the video)
              // - For images: use thumbnail as fallback
              if (isVideo) {
                console.log('Video full-size not found, signedUrl remains empty');
                signedUrl = '';
              } else {
                signedUrl = optData.signedUrl;
                console.log('Full-size image not found, using thumbnail for both');
              }
            }
          } 
          // If optimized version not found, try using the full size image for both
          else {
            console.log('Optimized image not found, trying full version');
            const { data: fullData, error: fullError } = await supabase
              .storage
              .from('generadas')
              .createSignedUrl(fullFilePath, 3600);
            
            if (!fullError && fullData?.signedUrl) {
              // Use full size image for the signed URL
              signedUrl = fullData.signedUrl;
              console.log('Found full-size image, using for both');
              
              // For videos, try to get the .png thumbnail from full folder
              if (isVideo) {
                const fullThumbnailPath = `${basePrefix}/full/${thumbnailFileName}`.replace(/\/+/, '/').replace(/\/+/g, '/');
                const { data: videoThumbData } = await supabase
                  .storage
                  .from('generadas')
                  .createSignedUrl(fullThumbnailPath, 3600);
                thumbnailUrl = videoThumbData?.signedUrl || fullData.signedUrl;
              } else {
                // Generate a thumbnail using the transform parameter for images
                const { data: thumbnailData } = await supabase
                  .storage
                  .from('generadas')
                  .createSignedUrl(fullFilePath, 3600, {
                    transform: {
                      width: 300,
                      height: 300,
                      resize: 'cover'
                    }
                  });
                
                thumbnailUrl = thumbnailData?.signedUrl || fullData.signedUrl;
              }
            } else {
              // Neither optimized nor full version found
              console.error('Image not found in any location:', img.file_name);
              signedUrl = '';
              thumbnailUrl = '';
            }
          }
        } catch (error) {
          console.error('Error processing image paths:', error);
          signedUrl = '';
          thumbnailUrl = '';
        }

        return {
          id: img.id,
          file_name: img.file_name,
          created_at: img.created_at,
          variante_id: img.variante_id,
          variante_nombre: img.variante_id ? (img.variantes?.nombre || '') : 'Imagen',
          formato: img.formato || '',
          tipo: img.tipo || '',
          favorito: img.favorito === true,
          signedUrl: signedUrl,
          thumbnailUrl: thumbnailUrl
        };
      }));

      // Check if we received less images than requested, meaning there are no more images to fetch
      setHasMoreImages(processedImages.length === PAGE_SIZE);
      
      // Process the images differently based on whether we're loading more or initial load
      if (isLoadingMore) {
        // If loading more, append the new images to existing ones
        setAllImages(prevImages => [...prevImages, ...processedImages]);
        setFilteredImages(prevImages => [...prevImages, ...processedImages]);
        // Update current page
        setCurrentPage(prev => prev + 1);
      } else {
        // Initial load - replace all images
        setAllImages(processedImages);
        setFilteredImages(processedImages);
        // Set page to 1 since we've loaded the first page
        setCurrentPage(1);
      }
      
      // Check if there are potentially more images to load
      setHasMoreImages(processedImages.length === PAGE_SIZE);
      
      setLoadingThumbnails(loadingThumbnailsMap);
    } catch (err) {
      console.error('Error fetching images:', err);
      setError(err instanceof Error ? err.message : 'Error loading images');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // The navigateImage function has been replaced with inline navigation in the arrow button click handlers

  const handleImageClick = (image: GeneratedImage) => {
    // Prevent default behavior to avoid unwanted navigation
    setSelectedImage(image);
    setZoomLevel(1);
    setDragOffset({ x: 0, y: 0 });
  };

  // State for download status overlay
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  const handleDownload = async (image: GeneratedImage | null, e?: React.MouseEvent) => {
    if (image && image.signedUrl) {
      try {
        // Prevent the default action to avoid navigation issues
        e?.preventDefault?.();
        
        // Show downloading status
        setIsDownloading(true);
        setDownloadSuccess(false);

        const isVideo = isVideoItem(image);
        
        const inferExtFromMime = (mime?: string): string => {
          if (!mime) return '';
          const m = mime.toLowerCase();
          if (m === 'image/png') return 'png';
          if (m === 'image/jpeg') return 'jpg';
          if (m === 'image/webp') return 'webp';
          if (m === 'image/gif') return 'gif';
          if (m === 'image/bmp') return 'bmp';
          if (m === 'image/tiff') return 'tiff';
          if (m === 'image/svg+xml') return 'svg';
          if (m === 'video/mp4') return 'mp4';
          if (m === 'video/webm') return 'webm';
          if (m === 'video/quicktime') return 'mov';
          return '';
        };

        const sanitizeDownloadFileName = (original?: string, mime?: string, forceVideo = false): string => {
          let name = (original || (forceVideo ? 'video' : 'imagen')).trim();
          if (forceVideo) {
            // For videos: remove ALL chained extensions like ".mp4.png" or ".webm.jpg"
            // Loop to remove extensions until none remain
            let prev = '';
            while (prev !== name) {
              prev = name;
              name = name.replace(/\.(png|jpe?g|webp|gif|bmp|tiff|svg|mp4|webm|mov)$/i, '');
            }
            return name + '.mp4';
          }
          name = name.replace(/\.(png|jpe?g|webp|gif|bmp|tiff|svg)_+$/i, '.$1');
          name = name.replace(/_+$/, '');
          const hasKnownExt = /\.(png|jpe?g|webp|gif|bmp|tiff|svg)$/i.test(name);
          if (!hasKnownExt) {
            const ext = inferExtFromMime(mime) || 'png';
            if (!/\.[a-z0-9]+$/i.test(name)) name = `${name}.${ext}`;
          }
          return name;
        };

        // For videos, skip enhancement and download directly
        if (isVideo) {
          const response = await fetch(image.signedUrl);
          if (!response.ok) throw new Error('Failed to fetch video');
          
const originalBlob = await response.blob();
// Force video MIME type if storage returned incorrect Content-Type (e.g., image/png for legacy videos)
const needsMimeFix = !originalBlob.type || !originalBlob.type.startsWith('video/');
const blob = needsMimeFix ? new Blob([originalBlob], { type: 'video/mp4' }) : originalBlob;
const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = sanitizeDownloadFileName(image.file_name, blob.type, true);
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
          }, 100);
          
          // Mark as downloaded in DB
          try {
            if (companyId) {
              await supabase
                .from('imagenes_generadas')
                .update({ descargada: true })
                .eq('id', image.id)
                .eq('id_company', companyId);
            }
          } catch {}
          
          setDownloadSuccess(true);
          setTimeout(() => {
            setIsDownloading(false);
            setDownloadSuccess(false);
          }, 3000);
          return;
        }

        // Get the user ID for the API request
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('No user found');
        
        // First try to enhance the image via the enhancement API
        try {
          const enhanceResponse = await fetch('https://ai-agency-dt7d.onrender.com/webhook/enhance', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              user_id: user.id, 
              image_file_name: image.file_name 
            })
          });
          
          if (enhanceResponse.ok) {
            const enhanceData = await enhanceResponse.json();
            if (enhanceData && enhanceData.url) {
              // Download the enhanced image
              const enhancedResponse = await fetch(enhanceData.url);
              if (enhancedResponse.ok) {
                const blob = await enhancedResponse.blob();
                
                // Create an object URL from the blob
                const blobUrl = URL.createObjectURL(blob);
                
                // Create a temporary link element
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = sanitizeDownloadFileName(image.file_name, blob.type);
                link.style.display = 'none'; // Hide the link
                
                // Append to the document, click it, and clean up
                document.body.appendChild(link);
                link.click();
                
                // Clean up
                setTimeout(() => {
                  document.body.removeChild(link);
                  URL.revokeObjectURL(blobUrl); // Free up memory
                }, 100);
                
                // Mark image as downloaded in DB
                try {
                  if (companyId) {
                    await supabase
                      .from('imagenes_generadas')
                      .update({ descargada: true })
                      .eq('id', image.id)
                      .eq('id_company', companyId);
                  }
                } catch {}
                
                // Show success message
                setDownloadSuccess(true);
                setTimeout(() => {
                  setIsDownloading(false);
                  setDownloadSuccess(false);
                }, 3000); // Hide after 3 seconds
                
                return; // Exit early since download was successful
              }
            }
          }
          
          // If we got here, enhancement failed in some way - fall back to direct download
          throw new Error('Enhancement failed or returned invalid data');
          
        } catch (enhanceErr) {
          console.error('Enhancement failed, falling back to direct download:', enhanceErr);
          
          // Fall back to direct download of original image
          const response = await fetch(image.signedUrl, {
            // Add cache control to prevent repeated requests
            cache: 'force-cache'
          });
          
          if (!response.ok) throw new Error('Failed to fetch image');
          
          const blob = await response.blob();
          
          // Create an object URL from the blob
          const blobUrl = URL.createObjectURL(blob);
          
          // Create a temporary link element
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = sanitizeDownloadFileName(image.file_name, blob.type);
          link.style.display = 'none'; // Hide the link
          
          // Append to the document, click it, and clean up
          document.body.appendChild(link);
          link.click();
          
          // Clean up
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl); // Free up memory
          }, 100);
          
          // Mark image as downloaded in DB
          try {
            if (companyId) {
              await supabase
                .from('imagenes_generadas')
                .update({ descargada: true })
                .eq('id', image.id)
                .eq('id_company', companyId);
            }
          } catch {}
          
          // Show success message even for fallback download
          setDownloadSuccess(true);
          setTimeout(() => {
            setIsDownloading(false);
            setDownloadSuccess(false);
          }, 3000); // Hide after 3 seconds
        }
      } catch (err) {
        console.error('Error downloading image:', err);
        alert('Error downloading image. Please try again.');
        setIsDownloading(false);
      }
    }
  };

  // Show deletion confirmation UI
  const showDeleteConfirmation = (image: GeneratedImage | null, e?: React.MouseEvent) => {
    if (!image) return;
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setImageToDelete(image);
    setShowDeleteConfirm(true);
  };
  
  // Cancel delete operation
  const cancelDelete = (e?: React.MouseEvent) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setShowDeleteConfirm(false);
    setImageToDelete(null);
  };
  
  // Delete image from Supabase storage and database
  const handleDeleteImage = async (e?: React.MouseEvent) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    
    if (!imageToDelete) return;
    
    try {
      setDeleting(true); // Show deleting state
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!profile?.id_company) throw new Error('No company associated with user');
      
      // Resolve id_company (UUID) to build storage path
      let idCompany = companyUuid || companyId || profile.id_company;
      if (!idCompany) throw new Error('No company UUID to build storage path');

      // Define the file paths for both full and optimized versions in 'generadas' bucket
      const fullFilePath = `${idCompany}/full/${imageToDelete.file_name}`;
      const optFilePath = `${idCompany}/opt/${imageToDelete.file_name}`;
      
      // Delete the image files from storage
      const { error: fullDeleteError } = await supabase
        .storage
        .from('generadas')
        .remove([fullFilePath]);
        
      if (fullDeleteError) {
        console.error('Error deleting full-size image:', fullDeleteError);
      }
      
      // Try to delete the optimized version too (if it exists)
      const { error: optDeleteError } = await supabase
        .storage
        .from('generadas')
        .remove([optFilePath]);
      
      if (optDeleteError) {
        console.error('Error deleting optimized image:', optDeleteError);
        // Continue even if optimized version couldn't be deleted
      }
      
      // Update the database record instead of deleting it
      // Set file_name to null and active to false
      const { error: dbUpdateError } = await supabase
        .from('imagenes_generadas')
        .update({ 
          file_name: null,
          active: false 
        })
        .eq('id', imageToDelete.id)
        .eq('id_company', profile.id_company);
        
      if (dbUpdateError) {
        throw dbUpdateError;
      }
      
      // Save current image position to navigate to next image after deletion
      const currentIndex = filteredImages.findIndex(img => img.id === imageToDelete.id);
      const nextIndex = currentIndex + 1 >= filteredImages.length ? 0 : currentIndex + 1;
      const nextImage = filteredImages.length > 1 ? filteredImages[nextIndex] : null;
      
      // Update the UI
      setAllImages(prevImages => prevImages.filter(img => img.id !== imageToDelete.id));
      setFilteredImages(prevImages => prevImages.filter(img => img.id !== imageToDelete.id));
      
      // Reset delete confirmation state
      setShowDeleteConfirm(false);
      setImageToDelete(null);
      
      // Navigate to next image if one exists, otherwise close the modal
      if (selectedImage && selectedImage.id === imageToDelete.id) {
        if (nextImage) {
          // Navigate to the next image
          setSelectedImage(nextImage);
          setZoomLevel(1);
          setDragOffset({ x: 0, y: 0 });
        } else {
          // No more images, close the modal
          setSelectedImage(null);
        }
      }
    } catch (err) {
      console.error('Error deleting image:', err);
      alert('Error al eliminar la imagen. Por favor, intenta de nuevo.');
    } finally {
      setDeleting(false);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoomLevel > 1) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setDragOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  // No longer need client-side applyFiltersToImages, as filtering is now server-side


  // Client-side filter function removed; filtering is handled server-side now

  useEffect(() => {
    if (selectedImage) {
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('mouseleave', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [selectedImage, isDragging]);

  // Set up intersection observer for infinite scrolling
  useEffect(() => {
    // Only set up the observer if there are more images to load
    if (!hasMoreImages || loadingMore || loading) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // When the observer element is visible, load more images
          fetchImages(true);
        }
      },
      { threshold: 0.5 } // Trigger when 50% of the element is visible
    );
    
    // Observe the load more trigger element if it exists
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
    
    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
      observer.disconnect();
    };
  }, [hasMoreImages, loadingMore, loading, filteredImages]);

  // Reset Instagram post success when closing the Instagram preview
  useEffect(() => {
    if (!showInstagramPreview) {
      setInstagramPostSuccess(false);
    }
  }, [showInstagramPreview]);

  useEffect(() => {
    // Prevent background scrolling when an image is selected
    if (selectedImage) {
      document.body.style.overflow = 'hidden';
      
      // Add keyboard event listener for arrow navigation and escape key
      const handleKeyDown = (e: KeyboardEvent) => {
        // Handle Escape key to close the fullscreen view
        if (e.key === 'Escape') {
          setSelectedImage(null);
          return;
        }
        
        if (filteredImages.length <= 1) return;
        
        const currentIndex = filteredImages.findIndex(img => selectedImage && img.id === selectedImage.id);
        if (currentIndex === -1) return;
        
        // Handle left arrow key - previous image
        if (e.key === 'ArrowLeft') {
          const prevIndex = currentIndex - 1 < 0 ? filteredImages.length - 1 : currentIndex - 1;
          const prevImage = filteredImages[prevIndex];
          setSelectedImage(prevImage);
          setZoomLevel(1);
          setDragOffset({ x: 0, y: 0 });
        }
        
        // Handle right arrow key - next image
        if (e.key === 'ArrowRight') {
          const nextIndex = currentIndex + 1 >= filteredImages.length ? 0 : currentIndex + 1;
          const nextImage = filteredImages[nextIndex];
          setSelectedImage(nextImage);
          setZoomLevel(1);
          setDragOffset({ x: 0, y: 0 });
        }
      };
      
      window.addEventListener('keydown', handleKeyDown);
      
      return () => {
        document.body.style.overflow = 'auto';
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
    return undefined;
  }, [selectedImage, isDragging, filteredImages]);

  // Unified Instagram connection prompt overlay (portal)
  const connectPrompt = (showConnectPrompt && !isInstagramConnected)
    ? createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100000] p-4" style={{ position: 'fixed' }}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
            <button 
              onClick={() => setShowConnectPrompt(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center justify-center mb-4">
              <div className="bg-blue-100 p-3 rounded-full">
                <Instagram className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-center mb-2">Conexión de Instagram requerida</h3>
            <p className="text-gray-600 text-center mb-6">
              Para poder publicar el contenido generado, primero debes conectar la cuenta de Instagram de la empresa a Atlas.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => setShowConnectPrompt(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => { navigate('/configuracion', { state: { section: 'instagram' } }); }}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md shadow-sm transition-colors flex items-center justify-center gap-2"
              >
                <Instagram className="w-4 h-4" />
                Conectar Instagram
              </button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="p-4 sm:p-8 relative">
      {/* Download Status Overlay */}
      {isDownloading && (
        <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex items-center justify-center px-4 mx-4">
          <div className="bg-white rounded-md shadow-lg p-4 flex flex-col items-center max-w-xs w-full">
            {downloadSuccess ? (
              <>
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-3">
                  <Check className="w-10 h-10 text-green-500" />
                </div>
                <p className="text-gray-800 font-medium text-center">Imagen descargada</p>
              </>
            ) : (
              <>
                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                <p className="text-gray-800 font-medium text-center">Preparando descarga</p>
              </>
            )}
          </div>
        </div>
      )}
      <div className="mb-8">
        {/* Mobile specific header layout with filter button in top right corner */}
        <div className="sm:hidden mb-4">
          <h1 className="text-sm font-semibold text-gray-900 text-center">Historial</h1>
          <button 
            onClick={() => setShowMobileFilters(true)}
            className="fixed top-4 right-4 z-30 flex-shrink-0 inline-flex items-center gap-2 px-4 h-9 rounded-full border border-gray-200 bg-white text-gray-900 shadow-sm hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <Filter className="w-4 h-4" />
            <span className="text-sm">Filtros</span>
          </button>
        </div>
        
        {/* Desktop header layout - match SeleccionarProductosAnuncio title style */}
        <div className="hidden sm:flex items-start justify-between gap-6 mb-6">
          <div className="flex-1 text-left">
            <h1 className="text-lg font-semibold text-gray-900 mt-4">Historial de imágenes generadas</h1>
          </div>
        </div>

        {/* Desktop Filters - hidden on mobile */}
        <div className="hidden md:block bg-white rounded-lg shadow-sm p-3 mb-4 relative">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Solo favoritos
              </label>
              <div className="flex items-center h-9">
                <button
                  type="button"
                  role="switch"
                  aria-checked={onlyFavorites}
                  onClick={() => {
                    setOnlyFavorites((prev) => !prev);
                    if (allImages.length > 0 && allImages.length % PAGE_SIZE === 0) {
                      setHasMoreImages(true);
                    }
                  }}
                  className={`relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${onlyFavorites ? 'bg-blue-500' : 'bg-gray-200'}`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${onlyFavorites ? 'translate-x-5' : 'translate-x-0'}`}
                  />
                </button>
                <span className="ml-3 text-sm text-gray-700">{onlyFavorites ? 'Activado' : 'Desactivado'}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Período de tiempo
              </label>
              <select
                value={selectedTimeframe}
                onChange={(e) => setSelectedTimeframe(e.target.value as TimeframeOption)}
                className="w-full px-4 pr-12 h-9 text-sm border border-gray-200 rounded-full bg-white text-gray-900 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <option value="today">Hoy</option>
                <option value="last7days">Últimos 7 días</option>
                <option value="last30days">Últimos 30 días</option>
                <option value="last90days">Últimos 90 días</option>
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Filtrar por producto
              </label>
              <select
                value={selectedVariant || ''}
                onChange={(e) => {
                  const newValue = e.target.value ? Number(e.target.value) : null;
                  console.log('Dropdown selected value:', e.target.value, 'as number:', newValue, 'type:', typeof newValue);
                  setSelectedVariant(newValue);
                  if (allImages.length > 0 && allImages.length % PAGE_SIZE === 0) {
                    setHasMoreImages(true);
                  }
                }}
                className="w-full px-4 pr-12 h-9 text-sm border border-gray-200 rounded-full bg-white text-gray-900 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <option value="">Todos los productos</option>
                {variants.map(variant => (
                  <option key={variant.id} value={variant.id}>
                    {variant.nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {/* Mobile Filter Panel */}
        {showMobileFilters && (
          <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40 flex flex-col justify-end transition-all">
            <div 
              className="bg-white rounded-t-xl p-4 pb-8 shadow-lg transform transition-transform duration-300 ease-in-out" 
              style={{ maxHeight: '80vh', overflowY: 'auto' }}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Filtros</h3>
                <button 
                  onClick={() => setShowMobileFilters(false)}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Solo favoritos
                  </label>
                  <div className="flex items-center">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={mobileOnlyFavorites}
                      onClick={() => {
                        setMobileOnlyFavorites((prev) => !prev);
                      }}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${mobileOnlyFavorites ? 'bg-blue-500' : 'bg-gray-200'}`}
                    >
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${mobileOnlyFavorites ? 'translate-x-5' : 'translate-x-0'}`}
                      />
                    </button>
                    <span className="ml-3 text-sm text-gray-700">{mobileOnlyFavorites ? 'Activado' : 'Desactivado'}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Período de tiempo
                  </label>
                  <select
                    value={mobileSelectedTimeframe}
                    onChange={(e) => setMobileSelectedTimeframe(e.target.value as TimeframeOption)}
                    className="w-full px-4 pr-12 h-10 text-sm border border-gray-200 rounded-full bg-white text-gray-900 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <option value="today">Hoy</option>
                    <option value="last7days">Últimos 7 días</option>
                    <option value="last30days">Últimos 30 días</option>
                    <option value="last90days">Últimos 90 días</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Filtrar por producto
                  </label>
                  <select
                    value={mobileSelectedVariant || ''}
                    onChange={(e) => {
                      const newValue = e.target.value ? Number(e.target.value) : null;
                      setMobileSelectedVariant(newValue);
                    }}
                    className="w-full px-4 pr-12 h-10 text-sm border border-gray-200 rounded-full bg-white text-gray-900 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <option value="">Todos los productos</option>
                    {variants.map(variant => (
                      <option key={variant.id} value={variant.id}>
                        {variant.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              
                <button
                  onClick={() => {
                    setSelectedTimeframe(mobileSelectedTimeframe);
                    setSelectedVariant(mobileSelectedVariant);
                    setOnlyFavorites(mobileOnlyFavorites);
                    // Reset pagination flag if needed – keep behavior aligned with desktop changes
                    if (allImages.length > 0 && allImages.length % PAGE_SIZE === 0) {
                      setHasMoreImages(true);
                    }
                    setShowMobileFilters(false);
                  }}
                  className="w-full mt-2 px-4 h-10 rounded-full bg-blue-500 hover:bg-blue-600 text-white shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Aplicar filtros
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="w-full aspect-square bg-gray-100 animate-pulse" />
              <div className="p-3">
                <div className="h-4 bg-gray-100 rounded w-2/3 animate-pulse mb-2" />
                <div className="h-3 bg-gray-100 rounded w-1/2 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredImages.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No se encontraron imágenes generadas</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredImages.map((image) => (
              <div
                key={image.id}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
              <div
                className="aspect-square cursor-pointer relative group"
                onClick={(e) => {
                  e.preventDefault(); // Prevent default to avoid unwanted navigation
                  e.stopPropagation(); // Stop event propagation
                  handleImageClick(image);
                }}
              >
                {/* Favorite (heart) toggle */}
                <button
                  onClick={(e) => handleToggleFavorite(image, e)}
                  disabled={isFavoriting}
                  aria-label={image.favorito ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                  className="absolute top-2 right-2 z-20 p-1.5 bg-white/90 hover:bg-white text-gray-700 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Heart className={image.favorito ? 'w-5 h-5 text-red-500' : 'w-5 h-5 text-gray-500'} fill={image.favorito ? 'currentColor' : 'none'} />
                </button>
                {(image.thumbnailUrl || image.signedUrl) && (
                  <>
                    {/* Show loading spinner while image is loading */}
                    {loadingThumbnails[image.id] && (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                        <div className="w-8 h-8 border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin"></div>
                      </div>
                    )}
                    
                    {/* Use thumbnail for grid view */}
                    <img
                      src={image.thumbnailUrl || image.signedUrl}
                      alt={`Generated image ${image.id}`}
                      className={`w-full h-full object-cover transition-transform group-hover:scale-105 ${
                        loadingThumbnails[image.id] ? 'opacity-40' : 'opacity-100'
                      }`}
                      loading="lazy"
                      width="300"
                      height="300"
                      crossOrigin="anonymous" // Add for cross-origin handling
                      referrerPolicy="no-referrer" // Prevent referrer info from being sent
                      onLoad={() => {
                        // When the image loads, remove the loading state
                        setLoadingThumbnails(prev => ({
                          ...prev,
                          [image.id]: false
                        }));
                      }}
                      onError={(e) => {
                        // If image fails to load, remove loading state and log error
                        console.error(`Failed to load image ${image.id}`, e);
                        setLoadingThumbnails(prev => ({
                          ...prev,
                          [image.id]: false
                        }));
                      }}
                    />
                    {/* Play icon overlay for videos */}
                    {isVideoItem(image) && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center">
                          <Play className="w-6 h-6 text-white fill-white" />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="p-2">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {image.variante_nombre}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {formatSmartDate(image.created_at)}
                </p>
              </div>
              </div>
            ))}
          </div>
          
          {/* Infinite scroll loading indicator - shown at bottom of page when more images available */}
          {hasMoreImages && filteredImages.length > 0 && (
            <div ref={loadMoreRef} className="flex justify-center mt-8 py-4">
              {loadingMore && (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                  <span className="text-gray-500">Cargando más imágenes...</span>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Instagram Post Preview Modal - FULL STRUCTURE MATCH */}
      {showInstagramPreview && selectedImage && (
        <div className="fixed inset-0 bg-gray-100 z-50 flex flex-col">
          {/* Header section */}
          <div className="px-4 py-3 bg-white shadow-sm z-20 sticky top-0 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Vista previa de Instagram</h2>
            <button 
              onClick={() => setShowInstagramPreview(false)}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-md flex items-center"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Main content area with scrolling */}
          <div className="flex-1 overflow-y-auto py-4 px-4 mt-2 relative">
            <div className="flex flex-col md:flex-row md:items-start md:justify-center md:space-x-6 max-w-5xl mx-auto pb-6">
              {/* Instagram post container */}
              <div className="bg-white rounded-md shadow-sm w-full max-w-sm mx-auto md:mx-0 overflow-hidden mb-6 md:mb-0 flex flex-col">
                {/* Post header with username and profile pic */}
                <div className="flex items-center p-3">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                    {companyLogo ? (
                      <img src={companyLogo} alt="Company logo" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                        {instagramUsername.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="ml-3">
                    <p className="font-semibold text-sm">{instagramUsername}</p>
                  </div>
                </div>
                {/* Post image with edge-to-edge display */}
                <div
                  ref={imageContainerRef as any}
                  className="w-full overflow-hidden"
                  style={{margin: 0, padding: 0, borderRadius: 0}}
                >
                  <img
                    src={selectedImage.signedUrl}
                    alt="Instagram post"
                    className="w-full"
                    style={{width: '100%', maxHeight: 'min(700px, calc(100vh - 200px))', display: 'block', margin: 0, padding: 0, borderRadius: 0}}
                    onLoad={e => {
                      const img = e.target as HTMLImageElement;
                      const aspectRatio = img.naturalWidth / img.naturalHeight;
                      const container = imageContainerRef.current;
                      if (!container) return;
                      if (aspectRatio === 1) {
                        container.style.aspectRatio = '1/1';
                        img.style.objectFit = 'contain';
                      } else if (aspectRatio > 1 && aspectRatio <= 1.91) {
                        container.style.aspectRatio = aspectRatio.toString();
                        img.style.objectFit = 'contain';
                      } else if (aspectRatio < 1 && aspectRatio >= 0.8) {
                        container.style.aspectRatio = aspectRatio.toString();
                        img.style.objectFit = 'contain';
                      } else if (aspectRatio < 0.8) {
                        container.style.aspectRatio = '0.8';
                        img.style.height = '100%';
                        img.style.width = 'auto';
                        img.style.maxWidth = 'none';
                        img.style.minWidth = '100%';
                        img.style.objectFit = 'cover';
                        img.style.objectPosition = 'center';
                        const scaledWidth = img.naturalHeight * 0.8;
                        if (scaledWidth < img.naturalWidth) {
                          img.style.objectPosition = '50% 50%';
                        }
                      } else {
                        container.style.aspectRatio = '1.91';
                        img.style.objectFit = 'contain';
                      }
                    }}
                  />
                </div>
                {/* Post interaction area */}
                <div className="p-3 border-t border-gray-100 mt-auto">
                  <div className="flex space-x-4">
                    <button className="text-gray-800"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" /></svg></button>
                    <button className="text-gray-800"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z" /></svg></button>
                    <button className="text-gray-800"><Share2 className="w-6 h-6" /></button>
                  </div>
                  <div className="mt-2">
                    <p className="font-semibold text-sm">127 Me gusta</p>
                  </div>
                  <div className="mt-1">
                    {isEditingDescription ? (
                      <div className="space-y-2">
                        <textarea
                          className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px]"
                          value={editedDescription}
                          onChange={e => setEditedDescription(e.target.value)}
                          placeholder="Escribe la descripción aquí..."
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setPostDescription(editedDescription);
                              setIsEditingDescription(false);
                            }}
                            className="px-3 py-1 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            Guardar
                          </button>
                          <button
                            onClick={() => {
                              setEditedDescription(postDescription);
                              setIsEditingDescription(false);
                            }}
                            className="px-3 py-1 bg-gray-200 text-gray-800 text-sm rounded-md hover:bg-gray-300 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm">
                        <span className="font-semibold mr-1">{instagramUsername}</span>
                        {postDescription}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              {/* Action buttons - to the right on desktop, below on mobile */}
              <div className="w-full max-w-sm mx-auto md:mx-0 md:w-60 md:flex-shrink-0 md:flex md:flex-col md:sticky md:top-1/2 md:-translate-y-1/2">
                <button
                  onClick={() => {
                    if (!isInstagramConnected) {
                      setShowConnectPrompt(true);
                      return;
                    }
                    handlePostToInstagram();
                  }}
                  className="w-full mb-2 h-10 rounded-full border border-gray-200 bg-white text-gray-900 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 px-4 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  disabled={isGeneratingDescription || isPostingToInstagram}
                >
                  {isPostingToInstagram ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                      <span>Publicando...</span>
                    </>
                  ) : instagramPostSuccess ? (
                    <>
                      <Check className="w-4 h-4 flex-shrink-0" />
                      <span>Publicado con éxito</span>
                    </>
                  ) : (
                    <>
                      {isInstagramConnected ? (
                        <Instagram className="w-4 h-4 flex-shrink-0" />
                      ) : (
                        <Lock className="w-4 h-4 flex-shrink-0" />
                      )}
                      <span>Publicar en Instagram</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setEditedDescription(postDescription);
                    setIsEditingDescription(true);
                  }}
                  className="w-full mb-2 h-10 rounded-full border border-gray-200 bg-white text-gray-900 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 px-4 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 flex-shrink-0"><path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" /></svg>
                  <span>Editar Descripción</span>
                </button>
                <button
                  onClick={() => handleDownload(selectedImage)}
                  className="w-full mb-2 h-10 rounded-full border border-gray-200 bg-white text-gray-900 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 px-4 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Download className="w-4 h-4 flex-shrink-0" />
                  <span>Descargar</span>
                </button>
                <button
                  onClick={() => copyDescription('preview')}
                  className={`w-full h-10 rounded-full transition-colors flex items-center justify-center gap-2 px-4 shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${copiedPreview ? 'bg-green-500 hover:bg-green-600 text-white' : 'border border-gray-200 bg-white text-gray-900 hover:bg-gray-50'}`}
                >
                  {copiedPreview ? <Check className="w-4 h-4 flex-shrink-0" /> : <Copy className="w-4 h-4 flex-shrink-0" />}
                  <span>{copiedPreview ? 'Copiado' : 'Copiar Descripción'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Full Screen Image View */}
      {selectedImage && selectedImage.signedUrl && !showInstagramPreview ? (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative w-full h-full" onClick={e => e.stopPropagation()}>
            {/* Floating favorite heart removed per request */}
            {/* Navigation Arrows */}
            <div className="absolute inset-y-0 left-4 flex items-center z-20">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (filteredImages.length > 1) {
                    const currentIndex = filteredImages.findIndex(img => selectedImage && img.id === selectedImage.id);
                    if (currentIndex !== -1) {
                      const prevIndex = currentIndex - 1 < 0 ? filteredImages.length - 1 : currentIndex - 1;
                      const prevImage = filteredImages[prevIndex];
                      setSelectedImage(prevImage);
                      setZoomLevel(1);
                      setDragOffset({ x: 0, y: 0 });
                    }
                  }
                }}
                className="p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
            </div>
            <div className="absolute inset-y-0 right-4 flex items-center z-20">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (filteredImages.length > 1) {
                    const currentIndex = filteredImages.findIndex(img => selectedImage && img.id === selectedImage.id);
                    if (currentIndex !== -1) {
                      const nextIndex = currentIndex + 1 >= filteredImages.length ? 0 : currentIndex + 1;
                      const nextImage = filteredImages[nextIndex];
                      setSelectedImage(nextImage);
                      setZoomLevel(1);
                      setDragOffset({ x: 0, y: 0 });
                    }
                  }
                }}
                className="p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
                aria-label="Next image"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </div>
            <div
              className="absolute inset-0 overflow-hidden flex items-center justify-center z-10"
              onMouseDown={!isVideoItem(selectedImage) ? handleMouseDown : undefined}
              onMouseMove={!isVideoItem(selectedImage) ? handleMouseMove : undefined}
              onMouseUp={!isVideoItem(selectedImage) ? handleMouseUp : undefined}
              onClick={(e) => {
                // Skip zoom behavior for videos
                if (isVideoItem(selectedImage)) return;
                
                // Only handle direct clicks (not drag operations)
                if (!isDragging) {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  // Find if the click was directly on the image, not the black sidebars
                  // Get the image element
                  const imgElement = e.currentTarget.querySelector('img');
                  if (!imgElement) return;
                  
                  // Get image boundaries
                  const imgRect = imgElement.getBoundingClientRect();
                  
                  // Check if click coordinates are within the image boundaries
                  const isClickOnImage = 
                    e.clientX >= imgRect.left && 
                    e.clientX <= imgRect.right && 
                    e.clientY >= imgRect.top && 
                    e.clientY <= imgRect.bottom;
                  
                  // Only zoom if click was directly on the image
                  if (isClickOnImage) {
                    if (zoomLevel === 1) {
                      // Zoom in at the clicked point
                      const container = e.currentTarget.getBoundingClientRect();
                      const containerCenterX = container.width / 2;
                      const containerCenterY = container.height / 2;
                      
                      // Calculate click position relative to the center
                      const clickX = e.clientX - container.left - containerCenterX;
                      const clickY = e.clientY - container.top - containerCenterY;
                      
                      // Zoom to 2x at the clicked point
                      setZoomLevel(2);
                      setDragOffset({ x: -clickX, y: -clickY });
                    } else {
                      // Zoom out to original size
                      setZoomLevel(1);
                      setDragOffset({ x: 0, y: 0 });
                    }
                  }
                }
              }}
              style={{ cursor: isVideoItem(selectedImage) ? 'default' : (zoomLevel > 1 ? 'move' : 'zoom-in'), pointerEvents: 'auto' }}
            >
              {isVideoItem(selectedImage) ? (
                <video
                  src={selectedImage.signedUrl}
                  controls
                  playsInline
                  autoPlay
                  className="pointer-events-auto"
                  style={{
                    maxWidth: '80%',
                    maxHeight: '80%',
                  }}
                >
                  Tu navegador no soporta la reproducción de video.
                </video>
              ) : (
                <img
                  src={selectedImage.signedUrl}
                  alt={`Full screen view of ${selectedImage.file_name}`}
                  className="pointer-events-none"
                  style={{
                    transform: `scale(${zoomLevel}) translate(${dragOffset.x}px, ${dragOffset.y}px)`,
                    maxWidth: '90%',
                    maxHeight: '90%',
                    transition: isDragging ? 'none' : 'transform 0.2s ease-out'
                  }}
                />
              )}
            </div>

            {/* Controls - Close button visible on all devices, zoom buttons hidden on mobile */}
            <div className="absolute top-4 right-4 flex flex-col space-y-2 z-30" style={{ pointerEvents: 'auto' }}>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedImage(null);
                }}
                className="p-2 bg-black bg-opacity-70 text-white rounded-full hover:bg-opacity-100 transition-colors cursor-pointer"
              >
                <X className="w-6 h-6" />
              </button>
              {/* Zoom buttons - Hidden on mobile */}
              <div className="hidden md:block">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setZoomLevel(prev => Math.min(prev + 0.25, 3));
                  }}
                  className="p-2 bg-black bg-opacity-70 text-white rounded-full hover:bg-opacity-100 transition-colors cursor-pointer mt-2"
                  disabled={zoomLevel >= 3}
                >
                  <Plus className="w-6 h-6" />
                </button>
              </div>
              <div className="hidden md:block">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
                  }}
                  className="p-2 bg-black bg-opacity-70 text-white rounded-full hover:bg-opacity-100 transition-colors cursor-pointer mt-2"
                  disabled={zoomLevel <= 0.5}
                >
                  <Minus className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-30 w-full max-w-md md:max-w-2xl px-4 md:px-0" style={{ pointerEvents: 'auto' }}>
              {/* Mobile layout: centered pill container with icon + small label below */}
              <div className="md:hidden flex justify-center">
                <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-1.5 shadow-sm">
                  {/* Favorite */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (selectedImage) handleToggleFavorite(selectedImage, e);
                    }}
                    disabled={isFavoriting}
                    className="px-2 py-1 flex flex-col items-center justify-center text-gray-700 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md"
                    aria-label={selectedImage?.favorito ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                  >
                    <Heart className={selectedImage?.favorito ? 'w-4 h-4 text-red-500' : 'w-4 h-4 text-gray-700'} fill={selectedImage?.favorito ? 'currentColor' : 'none'} />
                    <span className="text-xs mt-0.5">Favorito</span>
                  </button>
                  {/* Edit */}
                  <button
                    onClick={(e) => handleEditImage(selectedImage, e)}
                    className="px-2 py-1 flex flex-col items-center justify-center text-gray-700 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md"
                    aria-label="Editar imagen"
                  >
                    <Pencil className="w-4 h-4" />
                    <span className="text-xs mt-0.5">Editar</span>
                  </button>
                  {/* Download */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDownload(selectedImage, e);
                    }}
                    className="px-2 py-1 flex flex-col items-center justify-center text-gray-900 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md"
                    aria-label="Descargar"
                  >
                    <Download className="w-4 h-4" />
                    <span className="text-xs mt-0.5">Descargar</span>
                  </button>
                  {/* Description - hidden for videos */}
                  {!isVideoItem(selectedImage) && (
                    <button
                      onClick={handleShowDescription}
                      className="px-2 py-1 flex flex-col items-center justify-center text-gray-700 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md"
                      aria-label="Descripción"
                    >
                      <Instagram className="w-4 h-4" />
                      <span className="text-xs mt-0.5">Descripción</span>
                    </button>
                  )}
                  {/* Publish - temporarily disabled */}
                  {/*
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handlePostButton(selectedImage);
                    }}
                    className="px-2 py-1 flex flex-col items-center justify-center text-gray-700 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md"
                    aria-label="Publicar"
                  >
                    <Instagram className="w-4 h-4" />
                    <span className="text-xs mt-0.5">Publicar</span>
                  </button>
                  */}
                  {/* Delete */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      showDeleteConfirmation(selectedImage, e);
                    }}
                    className="px-2 py-1 flex flex-col items-center justify-center text-gray-700 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md"
                    aria-label="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="text-xs mt-0.5">Eliminar</span>
                  </button>
                </div>
              </div>

              {/* Desktop layout: all actions as pill buttons */}
              <div className="hidden md:flex flex-row justify-center gap-4">
                {/* Favorite */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (selectedImage) handleToggleFavorite(selectedImage, e);
                  }}
                  disabled={isFavoriting}
                  className="px-4 h-10 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-gray-900 transition-colors flex items-center justify-center gap-2 shadow-sm cursor-pointer focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Heart className={selectedImage?.favorito ? 'w-5 h-5 text-red-500' : 'w-5 h-5 text-gray-700'} fill={selectedImage?.favorito ? 'currentColor' : 'none'} />
                  <span className="text-sm md:text-base truncate">Favorito</span>
                </button>
                {/* Edit */}
                <button
                  onClick={(e) => handleEditImage(selectedImage, e)}
                  className="px-4 h-10 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-gray-900 transition-colors flex items-center justify-center gap-2 shadow-sm cursor-pointer focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Pencil className="w-5 h-5" />
                  <span className="text-sm md:text-base truncate">Editar</span>
                </button>
                {/* Download */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDownload(selectedImage, e);
                  }}
                  className="px-4 h-10 rounded-full border border-gray-200 bg-white text-gray-900 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 shadow-sm cursor-pointer focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Download className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm md:text-base truncate">Descargar</span>
                </button>
                {/* Description - hidden for videos */}
                {!isVideoItem(selectedImage) && (
                  <button
                    onClick={handleShowDescription}
                    className="px-4 h-10 rounded-full border border-gray-200 bg-white text-gray-900 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 shadow-sm cursor-pointer focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <Instagram className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm md:text-base truncate">Descripción</span>
                  </button>
                )}
                {/* Publish - temporarily disabled */}
                {/*
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handlePostButton(selectedImage);
                  }}
                  className="px-4 h-10 rounded-full border border-gray-200 bg-white text-gray-900 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 shadow-sm cursor-pointer focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Instagram className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm md:text-base truncate">Publicar</span>
                </button>
                */}
                {/* Delete */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    showDeleteConfirmation(selectedImage, e);
                  }}
                  className="px-4 h-10 rounded-full border border-gray-200 bg-white text-gray-900 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 shadow-sm cursor-pointer focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Trash2 className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm md:text-base truncate">Eliminar</span>
                </button>
              </div>
            </div>
            {showDescriptionPanel && selectedImage && (
              <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 z-40 w-[95vw] sm:w-full max-w-md px-4 md:px-0">
                <div className="relative bg-white rounded-2xl shadow-lg p-4">
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowDescriptionPanel(false); }}
                    className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md"
                    aria-label="Cerrar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  {isGeneratingDescription ? (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Generando descripción para post de Instagram</span>
                      {/* Bottom progress bar (inset and rounded to avoid protruding corners) */}
                      <div className="absolute left-3 right-3 bottom-0 h-1 rounded-full overflow-hidden bg-gray-100">
                        <div
                          className="h-full bg-blue-500"
                          style={{ width: progressActive ? '100%' : '0%', transition: 'width 20s cubic-bezier(0, 0, 0.2, 1)' }}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">Descripción para post</h3>
                      <div className="text-sm text-gray-800 whitespace-pre-wrap">{postDescription}</div>
                      <div className="mt-3 flex justify-end">
                        <button
                          onClick={() => copyDescription('panel')}
                          className={`${copiedPanel ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-500 hover:bg-blue-600'} h-9 px-4 text-white rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center gap-2`}
                        >
                          {copiedPanel ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          {copiedPanel ? 'Copiado' : 'Copiar'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Zoom Level Indicator - Only shown on desktop */}
            <div className="hidden md:block absolute bottom-20 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-70 text-white px-3 py-1 rounded-full">
              {Math.round(zoomLevel * 100)}%
            </div>
            
            {/* Delete Confirmation Dialog */}
            {showDeleteConfirm && imageToDelete && (
              <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[10000]" onClick={(e) => e.stopPropagation()}>
                <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-lg w-[95vw] sm:w-full max-w-md mx-4 sm:mx-0">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Confirmar eliminación</h3>
                  <p className="text-gray-700 mb-6">
                    ¿Estás seguro de que quieres eliminar esta imagen de <span className="font-semibold">{imageToDelete.variante_nombre}</span>?
                    <br />
                    <span className="text-red-600 text-sm">Esta acción no se puede deshacer.</span>
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                    {/* Delete first on mobile, centered */}
                    <button
                      onClick={handleDeleteImage}
                      className="h-10 px-4 bg-red-600 hover:bg-red-700 text-white rounded-full font-normal shadow-sm transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={deleting}
                    >
                      {deleting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Eliminando...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          Eliminar
                        </>
                      )}
                    </button>
                    <button
                      onClick={cancelDelete}
                      className="h-10 px-4 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50 transition-colors shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={deleting}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
      {connectPrompt}
      {showEditPlanRestriction && (
        <div className="fixed inset-0 flex items-center justify-center z-[100000]" style={{ position: 'fixed' }}>
          <div className="fixed inset-0 bg-black bg-opacity-60" onClick={() => setShowEditPlanRestriction(false)} />
          <div className="relative bg-white rounded-2xl w-[95vw] max-w-md p-6 shadow-lg">
            <button
              onClick={() => setShowEditPlanRestriction(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Editar con IA disponible en Plan Pro o Ultra</h3>
            <p className="text-gray-700 mb-5">La función de editar con IA está disponible para el plan Pro o Ultra. Mejorá tu plan para acceder a esta función y triplicar tu límite de fotos mensual.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button onClick={() => setShowEditPlanRestriction(false)} className="h-10 px-4 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50 transition-colors shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">Cancelar</button>
              <button onClick={() => { setShowEditPlanRestriction(false); navigate('/subscription'); }} className="h-10 px-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full font-normal shadow-sm transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">Ver planes</button>
            </div>
          </div>
        </div>
      )}
      <SubscriptionLimitModal
        isOpen={showSubscriptionLimitModal}
        onClose={() => setShowSubscriptionLimitModal(false)}
        type={subscriptionLimitType}
      />
    </div>
  );
};